/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  IconButton,
  Chip,
  Collapse,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { 
  FirstPage, 
  LastPage, 
  KeyboardArrowLeft, 
  KeyboardArrowRight,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon
} from '@mui/icons-material';
import useTableSort from '../../hooks/useTableSort';
import SortableTableHeader from '../shared/SortableTableHeader';
import useFacility from '../../hooks/useFacility';
import { toast } from 'react-toastify';
import RatePriceSchedule, { describeSchedule } from './RatePriceSchedule';
import RateBasisField from './RateBasisField';
import { formatRate } from '../../utils/ratePricing';

const formatRateBasis = (basis) => {
  if (!basis) return '/ day';
  return `/ ${basis}`;
};

const getUnitRateDisplay = (unit) => {
  const rates = unit.rates || [];
  if (rates.length === 0) return '-';
  if (rates.length === 1) {
    return `${formatRate(rates[0].rate)} ${formatRateBasis(rates[0].rate_basis)}`;
  }
  return `${rates.length} rates`;
};

// "$1.20 from Apr 1, 2027" for the next scheduled price change, or null.
const nextPriceChange = (rate) => {
  const next = describeSchedule(rate).upcoming[0];
  if (!next) return null;
  const [y, m, d] = String(next.effective_from).split('T')[0].split('-').map(Number);
  const when = new Date(y, m - 1, d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  return `${formatRate(next.rate)} from ${when}`;
};

const getUnitName = (unit) => {
  return unit.base_unit || '-';
};

const UnitManagement = () => {
  const { facilityPreference, loading: prefLoading } = useFacility();
  
  const [units, setUnits] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState('all');
  const [expandedUnit, setExpandedUnit] = useState(null);
  
  useEffect(() => {
    if (!prefLoading && facilityPreference) {
      setSelectedFacility(facilityPreference);
    }
  }, [facilityPreference, prefLoading]);
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addRateDialogOpen, setAddRateDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [newUnit, setNewUnit] = useState({
    base_unit: '',
    unit_short: '',
    facility: '',
    rates: [{ rate_id: 'default', display_name: 'Default Rate', rate: '', rate_basis: 'day' }]
  });
  const [newRate, setNewRate] = useState({
    rate_id: '',
    display_name: '',
    rate: '',
    rate_basis: 'day',
    description: ''
  });
  const [editRateDialogOpen, setEditRateDialogOpen] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  // Price schedule dialog: { unit, rateId }
  const [scheduleTarget, setScheduleTarget] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUnits, setFilteredUnits] = useState([]);

  const { sortedData, sortColumn, sortOrder, handleSort, resetSort } = useTableSort(
    filteredUnits,
    { defaultSortColumn: 'base_unit', defaultSortOrder: 'asc' }
  );

  useEffect(() => {
    fetchData();
  }, [selectedFacility]);

  useEffect(() => {
    const filtered = units.filter(unit => {
      const searchLower = searchQuery.toLowerCase();
      const unitName = getUnitName(unit).toLowerCase();
      const facilityName = unit.facility?.facility_name?.toLowerCase() || '';
      const rateMatch = unit.rates?.some(r => 
        r.display_name?.toLowerCase().includes(searchLower) ||
        r.rate_id?.toLowerCase().includes(searchLower)
      );
      return unitName.includes(searchLower) || 
             facilityName.includes(searchLower) ||
             rateMatch;
    });
    setFilteredUnits(filtered);
    setPage(0);
  }, [searchQuery, units]);

  const fetchData = async () => {
    try {
      const [unitsRes, facilitiesRes] = await Promise.all([
        axios.get('/api/units'),
        axios.get('/api/facilities')
      ]);
      
      let fetchedUnits = unitsRes.data;
      if (selectedFacility !== 'all') {
        fetchedUnits = fetchedUnits.filter(
          unit => unit.facility?._id === selectedFacility
        );
      }
      
      setUnits(fetchedUnits);
      setFilteredUnits(fetchedUnits);
      setFacilities(facilitiesRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error details:', error);
      setError('Failed to fetch data');
      setLoading(false);
    }
  };

  const startIndex = page * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, sortedData.length);
  const paginatedUnits = sortedData.slice(startIndex, endIndex);

  const handleCreate = async () => {
    try {
      if (!newUnit.base_unit || !newUnit.unit_short || !newUnit.facility) {
        alert('Please fill in all required fields');
        return;
      }

      const validRates = newUnit.rates.filter(r => r.rate_id && r.rate);
      if (validRates.length === 0) {
        alert('Please add at least one rate');
        return;
      }

      const normalizedRateIds = validRates.map(r => r.rate_id.toLowerCase().replace(/\s+/g, '_'));
      const duplicates = normalizedRateIds.filter((id, index) => normalizedRateIds.indexOf(id) !== index);
      if (duplicates.length > 0) {
        alert(`Duplicate Rate IDs found: ${[...new Set(duplicates)].join(', ')}. Each rate must have a unique Rate ID.`);
        return;
      }

      const unitData = {
        base_unit: newUnit.base_unit.trim(),
        unit_short: newUnit.unit_short.trim(),
        facility: newUnit.facility,
        rates: validRates.map((r) => ({
          rate_id: r.rate_id.toLowerCase().replace(/\s+/g, '_'),
          display_name: r.display_name || r.rate_id,
          rate: parseFloat(r.rate),
          rate_basis: r.rate_basis || 'day'
        }))
      };

      await axios.post('/api/units', unitData);
      await fetchData();
      toast.success('Unit created successfully');
      setNewUnit({
        base_unit: '',
        unit_short: '',
        facility: '',
        rates: [{ rate_id: 'default', display_name: 'Default Rate', rate: '', rate_basis: 'day' }]
      });
    } catch (error) {
      console.error('Error creating unit:', error);
      toast.error(error.response?.data?.error || 'Failed to create unit');
    }
  };

  const handleUpdate = async () => {
    try {
      const unitData = {
        base_unit: editingUnit.base_unit,
        unit_short: editingUnit.unit_short,
        facility: typeof editingUnit.facility === 'object' ? editingUnit.facility._id : editingUnit.facility,
        rates: editingUnit.rates || []
      };

      await axios.put(`/api/units/${editingUnit._id}`, unitData);
      await fetchData();
      setEditDialogOpen(false);
      setEditingUnit(null);
    } catch (error) {
      console.error('Error updating unit:', error);
      alert(error.response?.data?.error || 'Failed to update unit');
    }
  };

  const handleAddRate = async () => {
    try {
      if (!newRate.rate_id || !newRate.rate) {
        alert('Rate ID and Rate are required');
        return;
      }

      const normalizedNewRateId = newRate.rate_id.toLowerCase().replace(/\s+/g, '_');
      const existingRateIds = (editingUnit.rates || []).map(r => r.rate_id.toLowerCase());
      if (existingRateIds.includes(normalizedNewRateId)) {
        alert(`Rate ID "${normalizedNewRateId}" already exists in this unit. Please use a unique Rate ID.`);
        return;
      }

      await axios.post(`/api/units/${editingUnit._id}/rates`, {
        rate_id: normalizedNewRateId,
        display_name: newRate.display_name || newRate.rate_id,
        rate: parseFloat(newRate.rate),
        rate_basis: newRate.rate_basis || 'day',
        description: newRate.description
      });

      await fetchData();
      setAddRateDialogOpen(false);
      setNewRate({ rate_id: '', display_name: '', rate: '', rate_basis: 'day', description: '' });
      
      const updated = (await axios.get(`/api/units/${editingUnit._id}`)).data;
      setEditingUnit(updated);
    } catch (error) {
      console.error('Error adding rate:', error);
      alert(error.response?.data?.error || 'Failed to add rate');
    }
  };

  const handleDeleteRate = async (rateId) => {
    try {
      await axios.delete(`/api/units/${editingUnit._id}/rates/${rateId}`);
      await fetchData();
      const updated = (await axios.get(`/api/units/${editingUnit._id}`)).data;
      setEditingUnit(updated);
    } catch (error) {
      console.error('Error deleting rate:', error);
      alert(error.response?.data?.error || 'Failed to delete rate');
    }
  };

  const openEditRateDialog = (rate) => {
    setEditingRate({
      original_rate_id: rate.rate_id,
      rate_id: rate.rate_id,
      display_name: rate.display_name || '',
      rate: rate.rate?.toString() || '',
      rate_basis: rate.rate_basis || 'day',
      description: rate.description || ''
    });
    setEditRateDialogOpen(true);
  };

  const handleUpdateRate = async () => {
    try {
      if (!editingRate.rate_id || !editingRate.rate_id.trim()) {
        alert('Rate ID is required');
        return;
      }

      const normalizedNewRateId = editingRate.rate_id.toLowerCase().replace(/\s+/g, '_');
      const originalRateId = editingRate.original_rate_id.toLowerCase();
      
      if (normalizedNewRateId !== originalRateId) {
        const existingRateIds = (editingUnit.rates || []).map(r => r.rate_id.toLowerCase());
        if (existingRateIds.includes(normalizedNewRateId)) {
          alert(`Rate ID "${normalizedNewRateId}" already exists in this unit. Please use a unique Rate ID.`);
          return;
        }
      }

      await axios.put(`/api/units/${editingUnit._id}/rates/${editingRate.original_rate_id}`, {
        new_rate_id: normalizedNewRateId,
        display_name: editingRate.display_name || editingRate.rate_id,
        rate_basis: editingRate.rate_basis || 'day',
        description: editingRate.description
      });

      await fetchData();
      setEditRateDialogOpen(false);
      setEditingRate(null);
      const updated = (await axios.get(`/api/units/${editingUnit._id}`)).data;
      setEditingUnit(updated);
    } catch (error) {
      console.error('Error updating rate:', error);
      alert(error.response?.data?.error || 'Failed to update rate');
    }
  };

  const openPriceSchedule = (unit, rate) => setScheduleTarget({ unitId: unit._id, rateId: rate.rate_id });

  const handleScheduleUpdated = async (updatedUnit) => {
    setUnits(prev => prev.map(u => (u._id === updatedUnit._id ? updatedUnit : u)));
    if (editingUnit?._id === updatedUnit._id) setEditingUnit(updatedUnit);
  };

  const scheduleUnit = scheduleTarget ? units.find(u => u._id === scheduleTarget.unitId) : null;
  const scheduleRate = scheduleUnit?.rates?.find(r => r.rate_id === scheduleTarget?.rateId) || null;

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this unit?')) return;
    try {
      await axios.delete(`/api/units/${id}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting unit:', error);
      alert(error.response?.data?.error || 'Failed to delete unit');
    }
  };

  const handleChangePage = (newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const toggleExpand = (unitId) => setExpandedUnit(expandedUnit === unitId ? null : unitId);

  const renderRateBasisInput = (value, onChange, size = 'small') => (
    <RateBasisField value={value} onChange={onChange} size={size} compact />
  );

  const renderPaginationControls = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <span>Show</span>
        <Select value={rowsPerPage} onChange={handleChangeRowsPerPage} sx={{ mx: 1 }} size="small">
          {[10, 25, 50, 100].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </Select>
        <span>entries</span>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton aria-label="First page" onClick={() => handleChangePage(0)} disabled={page === 0}><FirstPage /></IconButton>
        <IconButton aria-label="Previous page" onClick={() => handleChangePage(page - 1)} disabled={page === 0}><KeyboardArrowLeft /></IconButton>
        <Box sx={{ mx: 2 }}>Page {page + 1} of {Math.max(1, Math.ceil(filteredUnits.length / rowsPerPage))}</Box>
        <IconButton aria-label="Next page" onClick={() => handleChangePage(page + 1)} disabled={page >= Math.ceil(filteredUnits.length / rowsPerPage) - 1}><KeyboardArrowRight /></IconButton>
        <IconButton aria-label="Last page" onClick={() => handleChangePage(Math.ceil(filteredUnits.length / rowsPerPage) - 1)} disabled={page >= Math.ceil(filteredUnits.length / rowsPerPage) - 1}><LastPage /></IconButton>
      </Box>
    </Box>
  );

  const renderRatesExpanded = (unit) => (
    <Collapse in={expandedUnit === unit._id}>
      <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Rates:</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Rate ID</TableCell>
              <TableCell>Display Name</TableCell>
              <TableCell>Current Price ($)</TableCell>
              <TableCell>Billing Basis</TableCell>
              <TableCell>Next Change</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(unit.rates || []).map((rate, idx) => (
              <TableRow key={rate.rate_id || idx}>
                <TableCell><Chip label={rate.rate_id} size="small" /></TableCell>
                <TableCell>{rate.display_name}</TableCell>
                <TableCell>{formatRate(rate.rate)}</TableCell>
                <TableCell>{formatRateBasis(rate.rate_basis)}</TableCell>
                <TableCell>{nextPriceChange(rate) || '—'}</TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => openPriceSchedule(unit, rate)}>Price Schedule</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Collapse>
  );

  const renderCreateDialog = () => (
    <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>Add New Unit</DialogTitle>
      <DialogContent>
        <TextField
          label="Base Unit Name" fullWidth margin="normal"
          placeholder="e.g., Square Feet, Cart, Cylinder"
          value={newUnit.base_unit}
          onChange={(e) => setNewUnit({...newUnit, base_unit: e.target.value})}
        />
        <TextField
          label="Unit Short" fullWidth margin="normal"
          placeholder="e.g., Sq Ft, Cart, Cyl"
          value={newUnit.unit_short}
          onChange={(e) => setNewUnit({...newUnit, unit_short: e.target.value})}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Facility</InputLabel>
          <Select value={newUnit.facility} onChange={(e) => setNewUnit({...newUnit, facility: e.target.value})} label="Facility">
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>{facility.facility_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Rates</Typography>
        {newUnit.rates.map((rate, idx) => (
          <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <TextField
              label="Rate ID" size="small" value={rate.rate_id}
              onChange={(e) => {
                const rates = [...newUnit.rates];
                rates[idx].rate_id = e.target.value;
                setNewUnit({...newUnit, rates});
              }}
              sx={{ width: '20%' }}
            />
            <TextField
              label="Display Name" size="small" value={rate.display_name}
              onChange={(e) => {
                const rates = [...newUnit.rates];
                rates[idx].display_name = e.target.value;
                setNewUnit({...newUnit, rates});
              }}
              sx={{ width: '25%' }}
            />
            <TextField
              label="Rate ($)" size="small" type="number" value={rate.rate}
              onChange={(e) => {
                const rates = [...newUnit.rates];
                rates[idx].rate = e.target.value;
                setNewUnit({...newUnit, rates});
              }}
              sx={{ width: '15%' }}
            />
            {renderRateBasisInput(
              rate.rate_basis,
              (basis) => {
                const rates = [...newUnit.rates];
                rates[idx].rate_basis = basis;
                setNewUnit({...newUnit, rates});
              }
            )}
            <Button
              size="small" color="error"
              onClick={() => setNewUnit({...newUnit, rates: newUnit.rates.filter((_, i) => i !== idx)})}
              disabled={newUnit.rates.length <= 1}
            >
              Remove
            </Button>
          </Box>
        ))}
        <Button
          startIcon={<AddIcon />}
          onClick={() => setNewUnit({
            ...newUnit,
            rates: [...newUnit.rates, { rate_id: '', display_name: '', rate: '', rate_basis: 'day' }]
          })}
        >
          Add Rate
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleCreate} variant="contained" color="primary">Create Unit</Button>
      </DialogActions>
    </Dialog>
  );

  const renderEditDialog = () => (
    <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>Edit Unit</DialogTitle>
      <DialogContent>
        <TextField
          label="Base Unit Name" fullWidth margin="normal"
          value={editingUnit?.base_unit || ''}
          onChange={(e) => setEditingUnit({...editingUnit, base_unit: e.target.value})}
        />
        <TextField
          label="Unit Short" fullWidth margin="normal"
          value={editingUnit?.unit_short || ''}
          onChange={(e) => setEditingUnit({...editingUnit, unit_short: e.target.value})}
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Facility</InputLabel>
          <Select
            value={editingUnit?.facility?._id || ''}
            onChange={(e) => setEditingUnit({...editingUnit, facility: e.target.value})}
            label="Facility"
          >
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>{facility.facility_name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
          Rates
          <IconButton aria-label="Add" size="small" onClick={() => setAddRateDialogOpen(true)}><AddIcon /></IconButton>
        </Typography>
        <Paper variant="outlined" sx={{ p: 1 }}>
          {(editingUnit?.rates || []).map((rate, idx) => (
            <Box key={rate.rate_id || idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip label={rate.rate_id} size="small" />
              <Typography sx={{ flex: 1 }}>{rate.display_name}</Typography>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontWeight: 'bold' }}>
                  {formatRate(rate.rate)} {formatRateBasis(rate.rate_basis)}
                </Typography>
                {nextPriceChange(rate) && (
                  <Typography variant="caption" color="primary">Next: {nextPriceChange(rate)}</Typography>
                )}
              </Box>
              <Button size="small" color="primary" onClick={() => openPriceSchedule(editingUnit, rate)}>Price Schedule</Button>
              <Button size="small" color="primary" onClick={() => openEditRateDialog(rate)}>Edit</Button>
              <Button size="small" color="error" onClick={() => handleDeleteRate(rate.rate_id)} disabled={editingUnit.rates?.length <= 1}>Delete</Button>
            </Box>
          ))}
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleUpdate} variant="contained" color="primary">Save Changes</Button>
      </DialogActions>
    </Dialog>
  );

  const renderAddRateDialog = () => (
    <Dialog open={addRateDialogOpen} onClose={() => setAddRateDialogOpen(false)}>
      <DialogTitle>Add Rate</DialogTitle>
      <DialogContent>
        <TextField
          label="Rate ID" fullWidth margin="normal"
          placeholder="e.g., academic, commercial, federal"
          value={newRate.rate_id}
          onChange={(e) => setNewRate({...newRate, rate_id: e.target.value})}
        />
        <TextField
          label="Display Name" fullWidth margin="normal"
          placeholder="e.g., Academic Rate, Commercial Rate"
          value={newRate.display_name}
          onChange={(e) => setNewRate({...newRate, display_name: e.target.value})}
        />
        <TextField
          label="Rate ($)" fullWidth margin="normal" type="number"
          inputProps={{ min: 0, step: 'any' }}
          value={newRate.rate}
          onChange={(e) => setNewRate({...newRate, rate: e.target.value})}
        />
        <RateBasisField
          fullWidth
          margin="normal"
          size="medium"
          value={newRate.rate_basis}
          onChange={(basis) => setNewRate({...newRate, rate_basis: basis})}
        />
        <TextField
          label="Description (optional)" fullWidth margin="normal" multiline rows={2}
          value={newRate.description}
          onChange={(e) => setNewRate({...newRate, description: e.target.value})}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAddRateDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleAddRate} variant="contained" color="primary">Add Rate</Button>
      </DialogActions>
    </Dialog>
  );

  const renderEditRateDialog = () => (
    <Dialog open={editRateDialogOpen} onClose={() => setEditRateDialogOpen(false)}>
      <DialogTitle>Edit Rate: {editingRate?.rate_id}</DialogTitle>
      <DialogContent>
        <TextField
          label="Rate ID" fullWidth margin="normal"
          value={editingRate?.rate_id || ''}
          onChange={(e) => setEditingRate({...editingRate, rate_id: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
          helperText="Lowercase, underscores for spaces (e.g., academic, commercial)"
        />
        <TextField
          label="Display Name" fullWidth margin="normal"
          placeholder="e.g., Academic Rate, Commercial Rate"
          value={editingRate?.display_name || ''}
          onChange={(e) => setEditingRate({...editingRate, display_name: e.target.value})}
        />
        <TextField
          label="Current price ($)" fullWidth margin="normal"
          value={editingRate?.rate || ''}
          disabled
          helperText="Change prices from Price Schedule, which records each price with the date it takes effect."
        />
        <RateBasisField
          fullWidth
          margin="normal"
          size="medium"
          value={editingRate?.rate_basis}
          onChange={(basis) => setEditingRate({...editingRate, rate_basis: basis})}
        />
        <TextField
          label="Description (optional)" fullWidth margin="normal" multiline rows={2}
          value={editingRate?.description || ''}
          onChange={(e) => setEditingRate({...editingRate, description: e.target.value})}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditRateDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleUpdateRate} variant="contained" color="primary">Save Changes</Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="unit-management">
      <h2>Unit Management</h2>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Units define measurement types (e.g., Square Feet, Carts) with multiple rate options (Academic, Commercial, etc.)
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search units..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 300 }}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} /> }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Facility</InputLabel>
          <Select value={selectedFacility} onChange={(e) => setSelectedFacility(e.target.value)} label="Filter by Facility">
            <MenuItem value="all">All Facilities</MenuItem>
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>{facility.facility_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Button variant="contained" color="primary" onClick={() => setCreateDialogOpen(true)}>Add New Unit</Button>
        <Button variant="outlined" onClick={resetSort}>Reset Sort</Button>
      </Box>

      <table className="data-table">
        <thead>
          <tr>
            <th style={{ width: 40 }}></th>
            <SortableTableHeader column="base_unit" label="Unit Type" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <SortableTableHeader column="unit_short" label="Abbreviation" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <th>Rate(s)</th>
            <SortableTableHeader column="facility.facility_name" label="Facility" currentSortColumn={sortColumn} sortOrder={sortOrder} onSort={handleSort} />
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedUnits.map(unit => (
            <React.Fragment key={unit._id}>
              <tr>
                <td>
                  {unit.rates?.length > 0 && (
                    <IconButton aria-label={expandedUnit === unit._id ? 'Hide rates' : 'Show rates'} size="small" onClick={() => toggleExpand(unit._id)}>
                      {expandedUnit === unit._id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  )}
                </td>
                <td>
                  <strong>{getUnitName(unit)}</strong>
                  {unit.rates?.length > 0 && (
                    <Box sx={{ mt: 0.5 }}>
                      {unit.rates.slice(0, 3).map(r => (
                        <Chip key={r.rate_id} label={r.rate_id} size="small" sx={{ mr: 0.5, mb: 0.5 }} color="default" />
                      ))}
                      {unit.rates.length > 3 && (
                        <Chip label={`+${unit.rates.length - 3} more`} size="small" variant="outlined" />
                      )}
                    </Box>
                  )}
                </td>
                <td>{unit.unit_short}</td>
                <td>
                  {getUnitRateDisplay(unit)}
                  {unit.rates?.some(r => nextPriceChange(r)) && (
                    <Box><Chip label="Price change scheduled" size="small" color="primary" variant="outlined" sx={{ mt: 0.5 }} /></Box>
                  )}
                </td>
                <td>{unit.facility?.facility_name}</td>
                <td className="action-buttons">
                  <Button
                    variant="contained" size="small"
                    className="row-action-edit"
                  style={{ marginRight: '8px' }}
                    onClick={() => { setEditingUnit(unit); setEditDialogOpen(true); }}
                  >
                    Edit
                  </Button>
                  <Button variant="contained" size="small" color="error" onClick={() => handleDelete(unit._id)}>
                    Delete
                  </Button>
                </td>
              </tr>
              <tr>
                <td colSpan={6} style={{ padding: 0 }}>
                  {renderRatesExpanded(unit)}
                </td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {renderPaginationControls()}
      {renderCreateDialog()}
      {renderEditDialog()}
      {renderAddRateDialog()}
      {renderEditRateDialog()}
      <RatePriceSchedule
        open={Boolean(scheduleRate)}
        unit={scheduleUnit}
        rate={scheduleRate}
        onClose={() => setScheduleTarget(null)}
        onUpdated={handleScheduleUpdated}
      />
    </div>
  );
};

export default UnitManagement;
